import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Conversations } from '../model';
import { fetchConversationByUuid } from '../../api/ConversationApi';
import './ConversationPage.scss';
import Badge from '../list/Badge';
import ChatContainer from './ChatContainer';
import MessageInput from './MessageInput';
import ConversationSidebar from './ConversationSidebar';
import { useConversationSocket } from './hooks/useConversationSocket';

const useConversationData = (uuid: string | undefined) => {
  const [conversation, setConversation] = useState<Conversations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!uuid) return;

    fetchConversationByUuid(uuid)
      .then(data => {
        setConversation(data);
        setIsLoaded(true);
      })
      .catch(() => setError('Failed to load conversation'));
  }, [uuid]);

  return { conversation, setConversation, error, isLoaded };
};

const ConversationPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { conversation, setConversation, error } = useConversationData(uuid);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false);

  const socketRef = useConversationSocket(
    uuid,
    setConversation,
    setIsNewMessage
  );

  useEffect(() => {
    setIsConnected(
      !!socketRef.current && socketRef.current.readyState === WebSocket.OPEN
    );
  }, [socketRef.current?.readyState]);

  const handleSendMessage = useCallback(() => {
    const ws = socketRef.current;
    if (newMessage && ws?.readyState === WebSocket.OPEN) {
      ws.send(newMessage);
      setNewMessage('');
    }
  }, [newMessage, socketRef]);

  if (error) return <div className="error">{error}</div>;

  return (
    <div className="conversation-page">
      <div className="conversation-header">
        <Badge badgeTitle={isConnected ? 'CONNECTED' : 'DISCONNECTED'} />
      </div>

      <div className="conversation-layout">
        <ChatContainer
          messages={conversation?.chatMessages || []}
          isNewMessage={isNewMessage}
        />
        <ConversationSidebar conversation={conversation} />
      </div>

      <MessageInput
        value={newMessage}
        onChange={setNewMessage}
        onSend={handleSendMessage}
        disabled={!isConnected}
      />
    </div>
  );
};

export default ConversationPage;
